import { api } from "./client";
import type {
  ColorVariant,
  DraftValidation,
  LifecycleResult,
  Product,
  ProductDetail,
  ProductListPage,
  Revision,
  RevisionHistory,
} from "./types";

/** Query parameters the backend's product listing actually accepts. */
export interface ProductListQuery {
  search?: string;
  status?: string;
  /** ONE stored category, matched exactly. Mutually exclusive with `family` —
   *  the backend refuses both rather than choosing between them. */
  category?: string;
  /** One commerce family, matched against the stored category the same way.
   *
   *  This is what makes a family view ONE request: Glasses is `sunglasses` and
   *  `eyeglasses`, and the server resolves that from the canonical taxonomy.
   *  Asking per category instead costs a request per category per page, which
   *  is a cost that grows with the page number. */
  family?: "glasses" | "contact_lenses" | "accessories";
  /** Which revision the page reports on, and which one every price, filter,
   *  search term and sort follows.
   *
   *  `live` (the default when omitted) is what customers see. `working` is the
   *  newest open draft — what an administrator is editing. They differ only
   *  for a published product with an open draft, which is exactly the shape a
   *  pricing surface exists to manage. */
  revision_scope?: "live" | "working";
  /** Whether the product carries a MONETARY discount on the revision the row
   *  reports: `on_sale` is a sale price that is set and below the base price —
   *  the same condition under which an order records a saving — and
   *  `not_on_sale` is everything else, a sale price equal to the base
   *  included. Omitted asks for both. The server decides; nothing here
   *  narrows a page after it arrives. */
  sale_state?: "on_sale" | "not_on_sale";
  sort?: string;
  page?: number;
  page_size?: number;
}

/** The fields both intakes share. Neither carries image bytes: an upload batch
 *  names photographs already in storage, and a draft-first product has none
 *  yet. */
interface CreateProductFields {
  name: string;
  base_price: number;
  currency?: string;
  sale_price?: number | null;
  category?: string | null;
  frame_shape?: string | null;
  audience?: string | null;
  product_code?: string | null;
  slug?: string | null;
  /** The ONE size a glasses entry is. Refused by the backend on any other
   *  family, so it is only ever sent for frames. */
  frame_size?: string | null;
  /** The product this one is another SIZE of, confirmed by a human. A UUID,
   *  never a name; the backend resolves it to that model's root. */
  model_of_product_id?: string | null;
}

/** Everything the backend accepts when a processed upload becomes a product. */
export interface CreateProductBody extends CreateProductFields {
  upload_batch_id: string;
  /**
   * Opening quantity per colour, keyed by the colour folder name.
   *
   * The batch carries its own colours, so they cannot be supplied as
   * `variants` the way a draft-first product supplies them — but their stock
   * still has to be answered once, at the moment each colour first exists.
   * The backend folds these keys through `normalize_color_key`, the same
   * function that keys the variants themselves, and hands each number to
   * `InventoryService.create_item` so the opening balance is a real movement.
   * A colour the map does not name opens at zero.
   */
  initial_stock?: Record<string, number>;
}

/** One colour a draft-first product is created with — DraftVariantRequest in
 *  app/api/admin_products.py. No expected_version: the revision is being made
 *  by this very call, so there is no token to be stale against. */
export interface DraftVariantBody {
  name: string;
  swatch_hex?: string;
  stock_quantity?: number;
  size_options?: string[];
  enabled?: boolean;
  display_order?: number | null;
}

/**
 * Creating a classified product before any photograph exists.
 *
 * `upload_batch_id` is absent, and that absence IS the discriminator the
 * backend branches on — the two intakes are mutually exclusive there, because
 * a processed folder already carries its own colours and supplying a second
 * answer beside it would be arbitrary. Which is why this is a separate body
 * type rather than an optional field on the one above: the two cannot be
 * expressed together by accident.
 */
export interface CreateDraftProductBody extends CreateProductFields {
  variants?: DraftVariantBody[];
}

export const productsApi = {
  list: (query: ProductListQuery, signal?: AbortSignal) =>
    api.get<ProductListPage>("/admin/products", { ...query }, signal),

  /** Creates the product and its first draft from an upload the worker has
   *  already finished. There is no image field: bytes went straight to storage. */
  create: (body: CreateProductBody) =>
    api.post<{ product: Product; draft_revision: Revision }>("/admin/products", body),

  /** Creates a classified product and its first draft with no photographs yet.
   *
   *  The only order a cosmetic lens or an accessory can be created in: how a
   *  photograph is treated is the family's decision, the family is read off the
   *  product, so the product has to exist before the first photograph arrives.
   *  Same endpoint as `create` — the backend chooses the intake by the absence
   *  of upload_batch_id, so this is not a second creation route. */
  createDraft: (body: CreateDraftProductBody) =>
    api.post<{ product: Product; draft_revision: Revision }>("/admin/products", body),

  detail: (productId: string, signal?: AbortSignal) =>
    api.get<ProductDetail>(`/admin/products/${productId}`, undefined, signal),

  revisions: (productId: string, signal?: AbortSignal) =>
    api.get<RevisionHistory>(
      `/admin/products/${productId}/revisions`,
      undefined,
      signal,
    ),

  /** Returns the existing draft when there is one, so this is safe to repeat. */
  openDraft: (productId: string) =>
    api.post<{ draft_revision: Revision }>(`/admin/products/${productId}/drafts`),

  updateDraft: (
    productId: string,
    body: Record<string, unknown> & { expected_version: number },
  ) => api.patch<{ draft_revision: Revision }>(`/admin/products/${productId}/draft`, body),

  addVariant: (
    productId: string,
    body: Record<string, unknown> & { expected_version: number; name: string },
  ) =>
    api.post<{ variant: ColorVariant }>(
      `/admin/products/${productId}/draft/variants`,
      body,
    ),

  updateVariant: (
    productId: string,
    variantId: string,
    body: Record<string, unknown> & { expected_version: number },
  ) =>
    api.patch<{ variant: ColorVariant }>(
      `/admin/products/${productId}/draft/variants/${variantId}`,
      body,
    ),

  removeVariant: (productId: string, variantId: string, expectedVersion: number) =>
    api.del<{ removed: boolean; variant_id: string }>(
      `/admin/products/${productId}/draft/variants/${variantId}`,
      { expected_version: expectedVersion },
    ),

  validateDraft: (productId: string) =>
    api.post<DraftValidation>(`/admin/products/${productId}/draft/validate`),

  publish: (
    productId: string,
    expectedProductVersion: number,
    expectedDraftVersion: number,
  ) =>
    api.post<LifecycleResult>(`/admin/products/${productId}/publish`, {
      expected_product_version: expectedProductVersion,
      expected_draft_version: expectedDraftVersion,
    }),

  unpublish: (productId: string, expectedProductVersion: number) =>
    api.post<LifecycleResult>(`/admin/products/${productId}/unpublish`, {
      expected_product_version: expectedProductVersion,
    }),

  republish: (productId: string, expectedProductVersion: number) =>
    api.post<LifecycleResult>(`/admin/products/${productId}/republish`, {
      expected_product_version: expectedProductVersion,
    }),

  archive: (productId: string, expectedProductVersion: number) =>
    api.post<LifecycleResult>(`/admin/products/${productId}/archive`, {
      expected_product_version: expectedProductVersion,
    }),
};
